import { FC, useCallback } from 'react';
import { Link } from '@remix-run/react';
import { Avatar } from '~/components/avatar';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { ImmersivePostItemAtoms } from '@discover/body/immersive-post/immersive-post-item/atoms';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ClickPosition } from '~/utils/click-position';
import { ModuleType } from '~/utils/module-type';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format';
import { Button, ButtonSize } from '@lemon8/design';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToArticleAuthor } from '~/utils/active/use-active-to-article';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';

import './style.scss';
import { useSEOHref } from '~/utils/build-seo-url';
import { UserDetailUtil } from 'shared/models';

export const AuthorBar: FC = () => {
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const formatNum = useNumFormatter();

  const article = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentArticle);
  const author = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentAuthor);
  const activeToCurrentArticleAuthor = useActiveToArticleAuthor(article);

  const authorHref = useSEOHref(UserDetailUtil.getPath(author));

  const authorObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.related_article,
        current_position: ClickPosition.main_author,
        group_id: article.groupId,
      });
    }, []),
  });

  const handleAuthorClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticleAuthor(ClickPosition.main_author);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_author,
        page_click_id: author.userId,
      }),
    },
    [activeToCurrentArticleAuthor, author],
  );

  const followBtnObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.related_article,
        current_position: ClickPosition.main_author_follow_btn,
        group_id: article.groupId,
      });
    }, []),
  });

  const handleButtonClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticleAuthor(ClickPosition.main_author_follow_btn);
      },
      defaultBehavior: () => {
        activeToCurrentArticleAuthor(ClickPosition.main_author_follow_btn);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_author_follow_btn,
        page_click_id: author.userId,
      }),
    },
    [activeToCurrentArticleAuthor, author],
  );

  return (
    <div className="author-bar">
      <object>
        <Link className="author" to={authorHref} ref={authorObservedRef} onClick={handleAuthorClick}>
          <Avatar src={author.avatar} />
          <div className="author-info">
            <span className="name">{author.nickName}</span>
            <span className="follower-count">
              {t('follower_count_plural', { num: author.followerCount }).replace('%d', formatNum(author.followerCount))}
            </span>
          </div>
        </Link>
      </object>
      <div className="follow" ref={followBtnObservedRef}>
        <Button variant="Secondary" size={ButtonSize.small} title={t('follow')} onClick={handleButtonClick} />
      </div>
    </div>
  );
};
